import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/DBContext';
import { 
  Layout, 
  Button, 
  Card, 
  Typography, 
  Spin, 
  Select, 
  Row, 
  Col, 
  message,
  Tag,
  Space
} from 'antd';
import { 
  LoadingOutlined, 
  UploadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import CreatePDFModal from './createPDFModal';
import EditPDFModal from './editPDFModal';
import DeletePDFModal from './deletePDFModal';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AdminPDFPage = () => {
  const { fetchPDFs, createPDF, updatePDF, deletePDF, uploadPDFFile } = useAuth();
  const [pdfs, setPdfs] = useState([]);
  const [filteredPdfs, setFilteredPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newPDF, setNewPDF] = useState({
    title: '',
    description: '',
    category: '',
    file: null
  });

  const categories = [
    { value: 'information', label: 'Information', color: 'blue' },
    { value: 'advice', label: 'Advice', color: 'gold' },
    { value: 'resource', label: 'Resources', color: 'green' },
    { value: 'guidance', label: 'Guidance', color: 'purple' }
  ];

  useEffect(() => {
    loadPDFs();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPdfs(pdfs);
    } else {
      setFilteredPdfs(pdfs.filter(pdf => pdf.category === selectedCategory));
    }
  }, [selectedCategory, pdfs]);

  const loadPDFs = async () => {
    setLoading(true);
    try {
      const fetchedPDFs = await fetchPDFs();
      setPdfs(fetchedPDFs);
      setFilteredPdfs(fetchedPDFs);
    } catch (error) {
      message.error('Failed to load PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePDF = async () => {
    if (!newPDF.title || !newPDF.description || !newPDF.category || !newPDF.file) {
      message.error('Please fill all fields and select a PDF file');
      return;
    }

    setSubmitting(true);
    try {
      // Upload PDF file
      const pdfUrl = await uploadPDFFile(newPDF.file);
      
      // Create PDF record
      const pdfData = {
        title: newPDF.title,
        description: newPDF.description,
        category: newPDF.category,
        pdfUrl: pdfUrl
      };

      await createPDF(pdfData);
      message.success('PDF uploaded successfully!');
      
      // Reload PDFs
      await loadPDFs();
      
      setShowCreateModal(false);
      setNewPDF({ title: '', description: '', category: '', file: null });
    } catch (error) {
      console.error('Error creating PDF:', error);
      message.error('Failed to upload PDF');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePDF = async (updatedData) => {
    setSubmitting(true);
    try {
      let pdfUrl = selectedPDF.pdfUrl;
      
      // If new file is uploaded, upload it
      if (updatedData.file) {
        pdfUrl = await uploadPDFFile(updatedData.file);
      }
      
      const pdfData = {
        title: updatedData.title,
        description: updatedData.description,
        category: updatedData.category,
        pdfUrl: pdfUrl
      };

      await updatePDF(selectedPDF.id, pdfData);
      message.success('PDF updated successfully!');
      
      await loadPDFs();
      setShowEditModal(false);
      setSelectedPDF(null);
    } catch (error) {
      console.error('Error updating PDF:', error);
      message.error('Failed to update PDF');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePDF = async () => {
    try {
      await deletePDF(selectedPDF.id);
      message.success('PDF deleted successfully!');
      setShowDeleteModal(false);
      setPdfs(pdfs.filter(pdf => pdf.id !== selectedPDF.id));
      setSelectedPDF(null);
    } catch (error) {
      console.error('Error deleting PDF:', error);
      message.error('Failed to delete PDF');
    }
  };

  const getCategoryTag = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? (
      <Tag color={cat.color}>{cat.label}</Tag>
    ) : null;
  };

  return (
    <section>
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12">
            <Title level={2}>PDF Resource Manager</Title>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setShowCreateModal(true)}
              className="float-right"
            >
              Upload PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: 200 }}
              placeholder="Filter by category"
            >
              <Option value="all">All Categories</Option>
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  {cat.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredPdfs.length === 0 ? (
              <Col span={24}>
                <Card>
                  <div className="text-center py-5">
                    <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    <p className="mt-3 text-muted">No PDFs found</p>
                  </div>
                </Card>
              </Col>
            ) : (
              filteredPdfs.map(pdf => (
                <Col key={pdf.id} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    actions={[
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setSelectedPDF(pdf);
                          setShowEditModal(true);
                        }}
                      >
                        Edit
                      </Button>,
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setSelectedPDF(pdf);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </Button>
                    ]}
                  >
                    <Card.Meta
                      title={
                        <div>
                          <Text strong>{pdf.title}</Text>
                          <div className="mt-2">
                            {getCategoryTag(pdf.category)}
                          </div>
                        </div>
                      }
                      description={
                        <div>
                          <Text type="secondary" ellipsis={{ rows: 2 }}>
                            {pdf.description}
                          </Text>
                          <div className="mt-2">
                            <a 
                              href={pdf.pdfUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600"
                            >
                              View PDF
                            </a>
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))
            )}
          </Row>
        )}
      </div>

      {/* Create Modal */}
      <CreatePDFModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewPDF({ title: '', description: '', category: '', file: null });
        }}
        onCreate={handleCreatePDF}
        newPDF={newPDF}
        setNewPDF={setNewPDF}
        submitting={submitting}
        categories={categories}
      />

      {/* Edit Modal */}
      {selectedPDF && (
        <EditPDFModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPDF(null);
          }}
          onUpdate={handleUpdatePDF}
          pdf={selectedPDF}
          submitting={submitting}
          categories={categories}
        />
      )}

      {/* Delete Modal */}
      <DeletePDFModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPDF(null);
        }}
        onDelete={handleDeletePDF}
        pdfTitle={selectedPDF?.title}
      />
    </section>
  );
};

export default AdminPDFPage; 